/**
 * Formatted dispatch document — the paper-style sheet used by both the
 * standalone print/PDF route (/lr-direct/dispatch/:loadId) and the broker's
 * dispatch page (/lr-direct/dispatches/won/:loadId).
 *
 * Pure presentational: no data fetching, no auth checks, no printing logic.
 * Callers pass already-resolved data. Layout mirrors LRDirectDispatchSheetPage
 * so what you see on screen matches what prints.
 */

import { format } from "date-fns";
import { Truck, Trophy } from "lucide-react";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";

export interface DispatchSheetStop {
  id?: string;
  stop_number: number;
  stop_type: string; // "pickup" | "delivery"
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  scheduled_date?: string | null;
  scheduled_time_start?: string | null;
  scheduled_time_end?: string | null;
  instructions?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  pickup_number?: string | null;
  reference_number?: string | null;
  pallet_count?: number | null;
  confidential_notes?: string | null;
}

export interface DispatchSheetLoad {
  load_number: string;
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  equipment_type: string;
  pickup_date: string | null;
  delivery_date?: string | null;
  commodity?: string | null;
  weight_lbs?: number | null;
  length_ft?: number | null;
  total_miles?: number | null;
  special_instructions?: string | null;
  shipper_notes_to_brokers?: string | null;
  awarded_at?: string | null;
}

export interface DispatchSheetBroker {
  company_name: string;
  contact_name: string;
  email?: string | null;
  phone?: string | null;
}

interface Props {
  load: DispatchSheetLoad;
  stops: DispatchSheetStop[];
  broker?: DispatchSheetBroker | null;
  awardedAmount?: number | null;
  totalMiles?: number | null;
  /** Optional wrapper class — the sheet itself is white/black, but callers can
   *  add outer spacing or shadow to make it feel like a page. */
  className?: string;
  /** When true, drops the outer `max-w-3xl` + horizontal centering so it fills
   *  a parent container that already handles layout. */
  fill?: boolean;
}

function fmtEquipment(type: string) {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtDate(dateStr: string | null | undefined) {
  if (!dateStr) return "—";
  const d = parseYYYYMMDDToLocalDate(dateStr);
  if (!d) return dateStr;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function fmtTimeWindow(start: string | null | undefined, end: string | null | undefined) {
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  return "Open";
}

/** Blank-safe text: renders an em dash when the value is empty/null. */
function orDash(v: string | number | null | undefined) {
  if (v === null || v === undefined) return "—";
  const s = String(v).trim();
  return s === "" ? "—" : s;
}

/** Placeholder stop so the base pickup + delivery structure always renders. */
function placeholderStop(stop_type: "pickup" | "delivery"): DispatchSheetStop {
  return { stop_number: 1, stop_type };
}

export function DispatchSheet({ load, stops, broker, awardedAmount, totalMiles, className, fill }: Props) {
  // Render the stops in TRUE route sequence (stop_number order) — pickups and
  // deliveries may interleave in any order (pick → drop → pick → drop). No
  // grouping: grouping picks-then-drops scrambled real multi-stop routes.
  const ordered = [...stops].sort((a, b) => (a.stop_number ?? 0) - (b.stop_number ?? 0));
  // Base pickup + delivery always render (blank if empty) when there are no
  // stops at all. Additional stops are never invented.
  const renderStops = ordered.length > 0 ? ordered : [placeholderStop("pickup"), placeholderStop("delivery")];

  const miles = totalMiles ?? load.total_miles ?? null;
  const ratePerMile =
    awardedAmount != null && miles ? awardedAmount / Number(miles) : null;


  return (
    <div
      className={
        "bg-white text-black " +
        (fill ? "w-full " : "max-w-3xl mx-auto ") +
        "p-3 sm:p-8 text-[12px] leading-snug break-words " +
        (className ?? "")
      }
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b-2 border-black pb-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Truck className="h-5 w-5" />
            <span className="text-lg font-bold tracking-tight">LANERUNNER</span>
            <span className="text-xs text-gray-600 uppercase tracking-widest">LR Direct</span>
          </div>
          <h1 className="text-2xl font-black uppercase tracking-tight">Dispatch Sheet</h1>
        </div>
        <div className="text-right text-[11px]">
          <p className="font-mono font-bold text-base">{load.load_number}</p>
          {load.awarded_at && (
            <p className="text-gray-700">
              Awarded {format(new Date(load.awarded_at), "MMM d, yyyy h:mm a")}
            </p>
          )}
          <p className="text-gray-500">
            Generated {format(new Date(), "MMM d, yyyy h:mm a")}
          </p>
        </div>
      </div>

      {/* Lane summary */}
      <SheetBlock title="Lane Summary">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-[12px] print:grid-cols-4">
          <Field label="Origin" value={`${load.origin_city}, ${load.origin_state}`} />
          <Field label="Destination" value={`${load.destination_city}, ${load.destination_state}`} />
          <Field label="Total Miles" value={miles ? `${Number(miles).toLocaleString()} mi` : "—"} />
          <Field label="Equipment" value={fmtEquipment(load.equipment_type)} />
        </div>
      </SheetBlock>

      {/* Equipment & cargo */}
      <SheetBlock title="Equipment & Cargo">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 print:grid-cols-4">
          <Field label="Commodity" value={orDash(load.commodity)} />
          <Field label="Weight" value={load.weight_lbs ? `${load.weight_lbs.toLocaleString()} lbs` : "—"} />
          <Field label="Length" value={load.length_ft ? `${load.length_ft} ft` : "—"} />
          <Field label="Pickup Date" value={fmtDate(load.pickup_date)} />
          <Field label="Delivery Date" value={fmtDate(load.delivery_date)} />
        </div>
      </SheetBlock>

      {renderStops.map((s, i) => (
        <StopBlock key={s.id ?? `${s.stop_type}-${i}`} stop={s} position={i + 1} />
      ))}


      <SheetBlock title="Load Notes & Instructions">
        <div className="mb-2">
          <p className="text-[10px] uppercase font-bold text-gray-600">Shipper Notes</p>
          <p className="text-[12px] whitespace-pre-line">{orDash(load.shipper_notes_to_brokers)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-gray-600">Special Instructions</p>
          <p className="text-[12px] whitespace-pre-line">{orDash(load.special_instructions)}</p>
        </div>
      </SheetBlock>


      {broker && (
        <SheetBlock title="Awarded Broker" accent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 print:grid-cols-2">
            <Field label="Company" value={broker.company_name} bold />
            <Field label="Contact" value={broker.contact_name} />
            <Field label="Phone" value={broker.phone ? formatPhoneNumber(broker.phone) : "—"} />
            <Field label="Email" value={broker.email || "—"} />
            {awardedAmount != null && (
              <>
                <Field label="Awarded Amount" value={`$${awardedAmount.toLocaleString()}`} bold />
                <Field
                  label="Rate / Mile"
                  value={ratePerMile != null ? `$${ratePerMile.toFixed(2)}` : "—"}
                />
              </>
            )}
          </div>
        </SheetBlock>
      )}

      {/* Footer */}
      <div className="mt-6 pt-3 border-t border-gray-300 text-[10px] text-gray-500 text-center">
        <Trophy className="inline h-3 w-3 mr-1" />
        Generated by LaneRunner LR Direct · Confidential dispatch information · Do not distribute
      </div>
    </div>
  );
}

/* ── Subcomponents ── */

function SheetBlock({
  title,
  children,
  accent = false,
}: {
  title: string;
  children: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div className={`mb-4 border ${accent ? "border-black bg-gray-50" : "border-gray-300"} rounded`}>
      <div
        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${
          accent ? "bg-black text-white" : "bg-gray-100 text-gray-700"
        }`}
      >
        {title}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function Field({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase font-semibold text-gray-600">{label}</p>
      {/* break-words + min-w-0: long emails / addresses / URLs must wrap, never
          get clipped by the sheet's overflow-hidden wrapper. */}
      <p className={`text-[12px] min-w-0 break-words [overflow-wrap:anywhere] ${bold ? "font-bold" : ""}`}>{value}</p>
    </div>
  );
}

/** `position` is the 1-based RENDER position — labels always reflect the real
 *  route order, never a stored ordinal, so they can never go stale. */
function StopBlock({ stop, position }: { stop: DispatchSheetStop; position: number }) {
  const isPickup = stop.stop_type === "pickup";
  const cityState = [stop.city, stop.state].filter(Boolean).join(", ");
  return (
    <SheetBlock title={`Stop ${position} — ${isPickup ? "Pickup" : "Delivery"}`}>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2 print:grid-cols-2">
        <Field
          label="Location"
          value={orDash(`${cityState}${stop.postal_code ? ` ${stop.postal_code}` : ""}`)}
          bold
        />
        <Field label="Date" value={fmtDate(stop.scheduled_date)} />
        <Field label="Facility" value={orDash(stop.name)} />
        <Field label="Time Window" value={fmtTimeWindow(stop.scheduled_time_start, stop.scheduled_time_end)} />
        <Field label="Address" value={orDash(stop.address)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2 pt-2 border-t border-gray-200 print:grid-cols-3">
        <Field label="Contact" value={orDash(stop.contact_name)} />
        <Field label="Phone" value={stop.contact_phone ? formatPhoneNumber(stop.contact_phone) : "—"} />
        <Field label="Email" value={orDash(stop.contact_email)} />
      </div>

      <div className="mb-2 pt-2 border-t border-gray-200">
        <p className="text-[10px] uppercase font-semibold text-gray-600">Instructions</p>
        <p className="text-[12px] italic whitespace-pre-line">{orDash(stop.instructions)}</p>
      </div>

      <div className="mt-2 pt-2 border-t-2 border-dashed border-gray-400 bg-yellow-50 -mx-3 -mb-3 px-3 pb-3 pt-3 rounded-b">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-800 mb-2">
          🔒 Confidential Dispatch Info
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 print:grid-cols-2">
          <Field label={isPickup ? "Pickup #" : "Delivery #"} value={orDash(stop.pickup_number)} bold />
          <Field label={isPickup ? "Reference #" : "Delivery Ref #"} value={orDash(stop.reference_number)} />
          <Field label="Pallet Count" value={stop.pallet_count != null ? String(stop.pallet_count) : "—"} />
        </div>
        <div className="mt-2">
          <p className="text-[10px] uppercase font-semibold text-gray-600">Confidential Notes</p>
          <p className="text-[12px] whitespace-pre-line">{orDash(stop.confidential_notes)}</p>
        </div>
      </div>


    </SheetBlock>
  );
}
